import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MediaViewer from '../MediaViewer';

const getYouTubeId = (url) => {
  if (!url) return '';
  
  // Handle youtu.be format
  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1].split('?')[0];
  }
  
  // Handle youtube.com format
  if (url.includes('youtube.com/watch?v=')) {
    return url.split('v=')[1].split('&')[0];
  }
  
  // Handle youtube.com/embed format
  if (url.includes('youtube.com/embed/')) {
    return url.split('embed/')[1].split('?')[0];
  }
  
  return '';
};

const GalleryPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/media');
        const data = await response.json();

        if (data.message === 'Media fetched successfully') {
          setMedia(data.media);
          setFilteredMedia(data.media);
        } else {
          toast.error('Failed to fetch media');
        }
      } catch (error) {
        console.error('Error fetching media:', error);
        toast.error('Failed to fetch media');
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, []);

  const handleFilter = (type) => {
    setActiveFilter(type);
    if (type === 'all') {
      setFilteredMedia(media);
    } else {
      setFilteredMedia(media.filter(item => item.type === type));
    }
  };

  const getCount = (type) => {
    if (type === 'all') return media.length;
    return media.filter(item => item.type === type).length;
  };

  const handleMediaClick = (item) => {
    setSelectedMedia({
      items: item.urls.map(url => ({
        type: item.type,
        urls: [url],
        title: item.title,
        description: item.description
      })),
      startIndex: 0
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Media Gallery</h1>
        </div>
        
        {/* Filter Tabs - Always visible */}
        <div className="flex justify-center mb-8">
          <ul className="nav nav-pills flex space-x-4" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link px-4 py-2 rounded-lg ${
                  activeFilter === 'all' ? 'bg-[#1A8D32] text-white' : 'bg-gray-200 text-gray-700 hover:bg-[#1E4C9A] hover:text-white'
                }`}
                onClick={() => handleFilter('all')}
              >
                All ({getCount('all')})
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link px-4 py-2 rounded-lg ${
                  activeFilter === 'image' ? 'bg-[#1A8D32] text-white' : 'bg-gray-200 text-gray-700 hover:bg-[#1E4C9A] hover:text-white'
                }`}
                onClick={() => handleFilter('image')}
              >
                Albums ({getCount('image')})
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link px-4 py-2 rounded-lg ${
                  activeFilter === 'video' ? 'bg-[#1A8D32] text-white' : 'bg-gray-200 text-gray-700 hover:bg-[#1E4C9A] hover:text-white'
                }`}
                onClick={() => handleFilter('video')}
              >
                Videos ({getCount('video')})
              </button>
            </li>
          </ul>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Media Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMedia.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow duration-300"
                  onClick={() => handleMediaClick(item)}
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.urls[0]}
                      alt={item.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', item.urls[0]);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="relative pb-[56.25%]">
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${getYouTubeId(item.urls[0])}`}
                        title={item.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                    {item.description && (
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    )}
                    <p className="text-gray-500 text-xs mt-2">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {filteredMedia.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No media items found</p>
              </div>
            )}
          </>
        )}

        {/* Media Viewer */}
        {selectedMedia && (
          <MediaViewer
            media={selectedMedia.items}
            onClose={() => setSelectedMedia(null)}
          />
        )}
      </div>
    </div>
  );
};

export default GalleryPage; 